import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants/networking.constants';
import { AlertSeverityEnum, UseMutationsParams } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { IComisionResp, ICommision } from '@/shared/interfaces/erp/comision/comision.interface';

const { get, post, put, remove } = erpApi();

///* tanSctack query
export const useFetchICommisions = (params?: getICommisionsParams, enabled = true) => {
  return useQuery({
    queryKey: ['ICommisions', ...Object.values(params ?? {})],
    queryFn: async () => {
      return getICommisions(params);
    },
    enabled,
  });
};

export const useGetICommision = (id: string) => {
  return useQuery({
    queryKey: ['ICommisions', id],
    queryFn: async () => {
      return getICommision(id);
    },
  });
};

export const useCreateICommision = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createICommision,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ICommisions'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Comisión creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateICommision = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateICommision,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ICommisions'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Comisión actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useRemoveICommision = () => {
  const queryClient = useQueryClient();
  const setConfirmDialogIsOpen = useUiStore.getState().setConfirmDialogIsOpen;

  return useMutation({
    mutationFn: removeICommision,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ICommisions'] });
    },
    onSettled: () => {
      setConfirmDialogIsOpen(false);
    },
  });
};

///* axios
export type getICommisionsParams = Partial<ICommision> & {
  page?: number;
  page_size?: number;

  filterByState?: boolean;
};

export type CreateICommisionParams = Omit<ICommision, 'id_comiciones'>;
export type UpdateICommisionParams = {
  id: string;
  data: CreateICommisionParams;
};

export const getICommisions = async (params?: getICommisionsParams) => {
  const stateParams = { ...params };

  // filterByState
  if (stateParams.filterByState === false && stateParams.estado === undefined) {
    delete stateParams.estado;
  } else if (stateParams.filterByState !== false) {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);

  return get<IComisionResp>(`${urlBackend}api/v1/comiciones/?${queryParams}`, true);
};

export const getICommision = async (id: string) => {
  try {
    return await get<ICommision>(`${urlBackend}api/v1/comiciones/${id}/`, true);
  } catch (error) {
    handleAxiosError(error);
  }
};

export const createICommision = async (data: CreateICommisionParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<ICommision>(`${urlBackend}api/v1/comiciones/`, data, true);
};

export const updateICommision = async ({ id, data }: UpdateICommisionParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<ICommision>(`${urlBackend}api/v1/comiciones/${id}/`, data, true);
};

export const removeICommision = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await remove<ICommision>(`${urlBackend}api/v1/comiciones/${id}/`, true);

  setAlert(true, res.message, res.status);
  return res;
};